import React from "react";

import clsx from "clsx";
import { useForm } from "react-hook-form";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  type Annotation,
  type NewAnnotation,
} from "@features/custom-annotations";
import { TertiaryButton } from "@shared/buttons";
import { TextAreaInput } from "@shared/textarea-input";
import { maxCharacterLength, notEmpty } from "@shared/validators";

import { AnnotationMetadata } from "../annotation-list";
import { useAnnotationParam } from "../hooks/use-annotation-param";

import { AnnotationCampaignType } from "./annotation-campaign-type";
import { AnnotationDatePicker } from "./annotation-date-picker";
import { AnnotationFormControl } from "./annotation-form-control";

interface Props {
  currentAnnotation: Annotation | NewAnnotation;
  clearCurrentAnnotation: () => void;
  isAnnotationsLoading: boolean;
  createAnnotation: (
    description: string,
    annotatedDate: string,
    endDate?: string,
    campaignType?: string,
  ) => Promise<any>;
  updateAnnotation: (
    id: string,
    description: string,
    annotatedDate?: string,
    endDate?: string,
    campaignType?: string,
  ) => Promise<any>;
  deleteAnnotation: (id: string) => Promise<void>;
  isMmm?: boolean;
}

export const AnnotationForm = ({
  currentAnnotation,
  clearCurrentAnnotation,
  createAnnotation,
  updateAnnotation,
  deleteAnnotation,
  isAnnotationsLoading,
  isMmm,
}: Props) => {
  const { closeFlyout, annotationParam } = useAnnotationParam();
  const prettyLogEventPage = usePrettyLogEventPage();
  const isEdit = "id" in currentAnnotation;

  let annotatedDate = currentAnnotation.annotatedDate;
  const isSameDate =
    annotationParam && annotationParam?.startDate === annotationParam?.endDate;
  if (!isEdit && isSameDate) {
    annotatedDate = annotationParam.startDate;
  }
  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<NewAnnotation>({
    defaultValues: {
      ...currentAnnotation,
      annotatedDate,
    },
  });

  const onSubmit = async (data: NewAnnotation) => {
    const { description, annotatedDate, endDate, campaignType } = data;
    if ("id" in currentAnnotation) {
      await updateAnnotation(
        currentAnnotation.id,
        description,
        annotatedDate,
        endDate,
        campaignType,
      );
      prettyLogEventPage({
        domain: "Annotations",
        action: "onSubmit",
        additionalActionInfo: "customer edits an annotation",
        data: {
          id: currentAnnotation.id,
          annotatedDate,
        },
      });
    } else {
      await createAnnotation(description, annotatedDate, endDate, campaignType);
      prettyLogEventPage({
        domain: "Annotations",
        action: "onSubmit",
        additionalActionInfo: "customer creates a new annotation",
        data: { annotatedDate },
      });
    }
    clearCurrentAnnotation();
  };

  return (
    <div>
      <div className="flex items-center justify-between mt-7 mb-1">
        <TertiaryButton
          className="-mx-2 flex items-center gap-1.5 font-bold"
          onClick={clearCurrentAnnotation}
        >
          <i className="fa-regular fa-sharp fa-arrow-left" />
          BACK
        </TertiaryButton>
        <TertiaryButton
          className="p-0 w-8 h-8 flex items-center justify-center group/button"
          onClick={closeFlyout}
          data-testid="close-button"
        >
          <i
            className={clsx(
              "fa-regular fa-sharp fa-times text-2xl",
              "text-interstellar group-hover/button:text-cosmic",
            )}
          />
        </TertiaryButton>
      </div>
      <h2 className="font-bold text-2xl">
        {isEdit ? "Edit" : "New"} {isMmm ? "calendar event" : "note"}
      </h2>
      <form
        className="flex flex-col space-y-4 mt-5 mb-1"
        onSubmit={handleSubmit(onSubmit)}
        data-testid="annotation-form"
      >
        <AnnotationDatePicker
          watch={watch}
          setValue={setValue}
          currentAnnotation={currentAnnotation}
          control={control}
          errors={errors}
        />
        <AnnotationCampaignType
          currentAnnotation={currentAnnotation}
          control={control}
          errors={errors}
        />
        <TextAreaInput
          {...register("description", {
            required: "Description is required",
            validate: {
              notEmpty: notEmpty(),
              maxCharacterLength: maxCharacterLength({ maxLength: 300 }),
            },
          })}
          defaultValue={currentAnnotation.description}
          errors={errors}
          rows={16}
          label="NOTE"
          placeholder={
            isMmm
              ? "Type your event name or description here..."
              : "Type your note here..."
          }
        />
        {"id" in currentAnnotation && (
          <AnnotationMetadata annotation={currentAnnotation} />
        )}
        <AnnotationFormControl
          className="mt-1"
          isSubmitting={isSubmitting}
          currentAnnotation={currentAnnotation}
          clearCurrentAnnotation={clearCurrentAnnotation}
          deleteAnnotation={deleteAnnotation}
          isAnnotationsLoading={isAnnotationsLoading}
        />
      </form>
    </div>
  );
};
